import { useEffect } from 'react';

export function isPrivacyPolicyPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === '/privacy';
}

export function PrivacyPolicyPage(): JSX.Element {
  useEffect(() => {
    document.title = 'Privacy | TCVS Vault';
  }, []);

  return (
    <main className="tcvs-privacy-main">
      <header className="tcvs-privacy-header">
        <a className="tcvs-brand" href="/" aria-label="TCVS Vault home">
          <span className="tcvs-brand-mark" aria-hidden="true" />
          TCVS Vault
        </a>
        <p>Privacy Notice</p>
      </header>

      <article className="tcvs-privacy-content">
        <h1>TCVS Vault Privacy Notice</h1>
        <p className="tcvs-privacy-effective">Effective August 30, 2026</p>
        <p>
          TCVS Vault is a trading-card inventory and marketplace operations application provided by
          LateralOps. This notice explains how TCVS handles information when an authorized user
          connects an eBay seller account.
        </p>

        <section>
          <h2>Information we process</h2>
          <p>
            TCVS processes the minimum information needed to operate connected selling workflows:
          </p>
          <ul>
            <li>the connected eBay seller account identifier and authorization credentials;</li>
            <li>
              listing information such as eBay item IDs, custom labels, titles, prices, images,
              quantities, status, and marketplace timestamps;
            </li>
            <li>
              order information such as order and line-item identifiers, sold amounts, fulfillment
              status, and event timestamps; and
            </li>
            <li>TCVS card records, images, storage locations, and marketplace associations.</li>
          </ul>
          <p>
            TCVS does not intentionally retain buyer names, street addresses, payment-instrument
            information, or raw eBay notification bodies.
          </p>
        </section>

        <section>
          <h2>How we use information</h2>
          <p>We use this information to:</p>
          <ul>
            <li>reconcile TCVS inventory with eBay listings and orders;</li>
            <li>identify sold cards that need to be picked and fulfilled;</li>
            <li>prevent accidental duplicate marketplace listings;</li>
            <li>provide operational history, exceptions, and pricing context; and</li>
            <li>secure, monitor, and troubleshoot the integration.</li>
          </ul>
          <p>We do not sell personal information or use it for third-party advertising.</p>
        </section>

        <section>
          <h2>Storage, security, and retention</h2>
          <p>
            TCVS runs on access-controlled AWS infrastructure. Seller authorization credentials are
            encrypted and restricted to the services that synchronize eBay data. Operational records
            are retained while needed for inventory, sales, reconciliation, audit, and legal
            obligations, then removed when they are no longer required.
          </p>
        </section>

        <section>
          <h2>Sharing</h2>
          <p>
            Information is shared only with eBay and infrastructure providers as necessary to
            operate TCVS. We may also disclose information when required by law or to protect the
            security and integrity of the service.
          </p>
        </section>

        <section>
          <h2>Your choices</h2>
          <p>
            An authorized seller can revoke TCVS access through eBay account settings or ask us to
            disconnect the account. eBay marketplace account-deletion notifications are processed
            through TCVS's compliance endpoint.
          </p>
        </section>

        <section>
          <h2>Contact</h2>
          <p>
            Questions or requests about this notice can be sent to{' '}
            <a href="mailto:nate@lateralops.com">nate@lateralops.com</a>.
          </p>
        </section>
      </article>
    </main>
  );
}
